import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setSubmitted(true);
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData(form);
      formData.delete('company_alt');
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value.toString());
      });
      await fetch('https://readdy.ai/api/form/d8tnbtob9jno5e72bnc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // silently handle
    }
    setSubmitting(false);
    setSubmitted(true);
    setEmail('');
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <footer className="bg-background-200/70">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-16">
        <div className="flex flex-col lg:flex-row gap-10">
          <div className="lg:w-2/5">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              USFans<span className="text-accent-500">.</span> Polska
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-6 max-w-sm">
              {t('footer.disclaimer')}
            </p>
            <div>
              <p className="text-xs text-foreground-500 font-medium uppercase tracking-wider mb-3">{t('footer.newsletter_desc')}</p>
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.newsletter_placeholder')}
                  required
                  className="w-full px-0 py-2.5 text-sm bg-transparent border-b border-foreground-300 focus:border-foreground-700 outline-none transition-colors text-foreground-800 placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-foreground-700 border border-foreground-300 hover:bg-foreground-100 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  {submitted ? '✓ Zapisz się' : submitting ? 'Wysyłanie...' : t('footer.newsletter_button')}
                </button>
              </form>
            </div>
          </div>

          <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-4">{t('footer.categories')}</h4>
              <ul className="space-y-2.5">
                {categories.slice(0, 8).map((cat) => (
                  <li key={cat.slug}>
                    <a href={cat.url} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap">
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-4">{t('footer.articles')}</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/artykul/usfans-spreadsheet-kompletny-przewodnik" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap">
                    USFans Spreadsheet — Kompletny Przewodnik
                  </Link>
                </li>
                {categories.slice(8).map((cat) => (
                  <li key={cat.slug}>
                    <a href={cat.url} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap">
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-4">{t('footer.info')}</h4>
              <ul className="space-y-2.5">
                <li><Link to="/" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap">{t('nav.home')}</Link></li>
                <li><a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap">{t('cta.usfans_spreadsheet')}</a></li>
                <li><a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap">{t('cta.buy_now')}</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-300/60 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">© 2026 USFans Polska. {t('footer.rights')}</p>
          <p className="text-xs text-foreground-500">{t('footer.disclaimer')}</p>
        </div>
      </div>
    </footer>
  );
}